import { useTranslation } from "@localization/useTranslation";
import { getEnumValues } from "@common/enumOperations";
import { Guid } from "@common/guid";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "@common/stringOperations";
import { AnnualWheelPriority } from "../annualWheelDialog/AnnualWheelDialog.types";
import { RelationViewModel } from "../../taskManagement/createTaskPane/CreateTaskPane.types";
import { ActivityFormProps } from "./ActivityForm.types";

export type ActivityBasicsSectionProps = Pick<
    ActivityFormProps,
    "model" | "onModelChange" | "showError" | "readOnly" | "showTrialWarning" | "annualWheelActivityId"
>;

export function useActivityBasicsSection(props: ActivityBasicsSectionProps) {
    const { translateString } = useTranslation();

    const priorityOptions = getEnumValues(AnnualWheelPriority).map((priority) => ({
        id: priority.toString(),
        name: translateString(setFirstLetterToLowerCase(AnnualWheelPriority[priority].toString())),
    }));

    const model = props.model;

    function onNameChange(name: string) {
        props.onModelChange({ name });
    }

    function onDescriptionChange(description: string, mentionedRelations: Array<RelationViewModel>) {
        const relationsFromDescription = [
            ...model.relationsFromDescription,
            ...mentionedRelations.map((relation) => ({ ...relation, relationId: Guid.newGuid().toString() })),
        ];
        props.onModelChange({ description, relationsFromDescription });
    }

    function onCustomIdChange(customId: string) {
        props.onModelChange({ customId });
    }

    function onPriorityChange(priority: string | null) {
        props.onModelChange({ priority: isNullOrWhitespace(priority ?? undefined) ? undefined : Number(priority) });
    }

    function onDocumentationRequiredChange(documentationRequired: boolean) {
        props.onModelChange({ documentationRequired });
    }

    return {
        priorityOptions,
        showTitleError: props.showError && model.isNameMissing(),
        onNameChange,
        onDescriptionChange,
        onCustomIdChange,
        onPriorityChange,
        onDocumentationRequiredChange,
    };
}
